import { FC, useState } from 'react'
import { observer } from 'mobx-react'
import Draggable, { DraggableData, DraggableEvent } from 'react-draggable'
import _debug from 'debug'

import { fmt } from '../utils/Fmt'
import { GlossInfo } from '../../models3/GlossInfo'
import { Passage, PassageGloss, PassageVideo } from '../../models3/ProjectModels'
import { Root } from '../../models3/Root'
import { useEffect } from 'react'

const log = _debug('sltt:GlossLabel')

/** Update passageGloss to match video timeline currentTime */
function usePassageGloss(passage: Passage | undefined | null, passageVideo: PassageVideo | null, currentTime: number, glossInfo: GlossInfo): { passageGloss: PassageGloss | null } {
    const [passageGloss, setPassageGloss] = useState<PassageGloss | null>(null)

    /** True if i'th gloss matches current time */
    function glossMatchesTime(i: number, visibleGlosses: PassageGloss[], _currentTime: number): boolean {
        if (i < 0 || i >= visibleGlosses.length) return false
        const gloss = visibleGlosses[i]
        if (currentTime < gloss.time) return false
        if (i+1 >= visibleGlosses.length) return true
        const nextGloss = visibleGlosses[i+1]
        return currentTime < nextGloss.time
    }

    /** 
     * Update passageGloss when currentTime changes.
     * This is the gloss that is centered at the current time.
     */
    useEffect(() => {
        // If we are editing a gloss, inhibit updates in order to avoid
        // changing what gloss is being edited.
        if (glossInfo.editing) {
            setPassageGloss(glossInfo.passageGloss)
            return
        }

        if (!passage || !passageVideo) {
            setPassageGloss(null)
            glossInfo.set(null)
            return
        }

        const _visibleGlosses = passageVideo.getVisibleGlosses(passage)

        for (let i = 0; i < _visibleGlosses.length; i++) {
            if (glossMatchesTime(i, _visibleGlosses, currentTime)) {
                setPassageGloss(_visibleGlosses[i])
                glossInfo.set(_visibleGlosses[i])
                return
            }
        }

        // If we get here, no gloss matches the current time.
        setPassageGloss(null)
        glossInfo.set(null)
    }, 
    [passageVideo, passageVideo?._rev, currentTime])

    return { passageGloss }
}

function setLabelPosition(xy: number[], setXy: (xy: number[]) => void, data: DraggableData) {
    const { parentElement } = data.node
    if (!parentElement) return

    const { offsetWidth, offsetHeight } = parentElement
    // x and y are the amount the mouse moved in pixels
    const { x, y } = data

    // x0, y0, x2, y2 are percentages of the parent element's width and height
    const x2 = (x / offsetWidth) * 100
    const y2 = (y / offsetHeight) * 100

    const [x0, y0] = xy

    log('setLabelPosition', fmt({ x, y, offsetWidth, offsetHeight, x2, y2, x0, y0 }))

    setXy([x0 + x2, y0 + y2])
}

function textZoomToFontSize(textZoom: number, videoWidth: number) {
    return `${textZoom * videoWidth / 25}px`
}

function labelStyle(xy: number[], textZoom: number, videoWidth: number) {
    const [x, y] = xy
    const style = {
        left: `${x}%`,
        top: `${y}%`,
        fontSize: textZoomToFontSize(textZoom, videoWidth),
    }
    // log('labelStyle', fmt({x, y, textZoom, videoWidth, fontSize}))

    return style
}

interface IShowGlossLabelRoot {
    passage: Passage | null,
    passageVideo: PassageVideo | null,
    currentTime: number,
    glossInfo: GlossInfo,
}

interface IShowGlossLabel {
    rt: IShowGlossLabelRoot,
    videoWidth: number,
}

const ShowGlossLabel: FC<IShowGlossLabel> = ({ rt, videoWidth }) => {
    const { passage, passageVideo, currentTime, glossInfo } = rt
    const { hasLabel, editing, text, textZoom, xy, setXy, glossLabelRef } = glossInfo
    
    // Force re-render on drag stop to clear transform.
    // This is a workaround for the fact that react-draggable does not clear the transform.
    const [renderCount, setRenderCount] = useState(0)

    const { passageGloss } = usePassageGloss(passage, passageVideo, currentTime, glossInfo)

    if (editing) {
        log('###ShowGlossLabel we are editing a gloss label but there is none to display!')
        if (!passageGloss) return null
    } else {
        if (!passageGloss || !hasLabel || !text) return null
    }

    const style = labelStyle(xy, textZoom, videoWidth)

    if (!editing) {
        return (<div className="video-label" style={style}>{text}</div>)
    }

    return (
        <Draggable
            key={renderCount}
            bounds={'parent'}
            defaultClassName="draggable-label"
            defaultClassNameDragging="draggable-label-dragging"
            onStop={(e: DraggableEvent, data: DraggableData) => {
                setLabelPosition(xy, setXy, data)
                setRenderCount((count) => count + 1) // force re-render to clear transform
            }}
        >
            <div className="video-label" style={style} ref={glossLabelRef}>{text}</div>
        </Draggable>
    )
}

export default observer(ShowGlossLabel)